import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { NoDashboardSurface } from "../components/NoDashboardSurface";
import { PageError } from "../components/PageError";
import { PageSkeleton } from "../components/PageSkeleton";
import { useRecipe } from "../lib/api/queries";
import { resolveHomeSurface } from "../lib/capabilities";
import { useQueryView } from "../lib/loading/useQueryView";
import { Overview, OverviewHeader, OverviewSkeleton } from "./Overview";

// Resolves the index route ("/") by capability — never by recipe.slug
// (§0.4(6)). ADR-0072 Phase B: a bespoke postventa KPI overview was
// explicitly cut from this pass (no data path for two of its four
// tiles without a second backend aggregate) — group-rooted verticals
// redirect straight to /proyectos instead.
//
// While the recipe resolves the surface is unknown, and this route also
// lands postventa and unconfigured verticals (index and catch-all), so
// the frame drawn then is vertical-neutral: the overview's boxes (header,
// KPI tiles, panel) with every facility label as a bar — never «Tiendas…»
// copy in front of a postventa user. It goes through the loading
// contract, so a failed /recipes/me ends in an error with Reintentar,
// never an endless skeleton. A redirect (postventa → /proyectos) fires the
// moment the recipe lands, without waiting out the skeleton's minimum
// hold — nothing of this screen is about to be shown.
export function OverviewRoute() {
  const recipeQuery = useRecipe();
  const view = useQueryView(recipeQuery);
  // Whether this route's skeleton ever painted — handed to Overview so
  // its own dashboard skeleton continues it instead of blinking.
  const [skeletonShown, setSkeletonShown] = useState(false);
  const painted = view.kind === "loading" && view.visible;
  useEffect(() => {
    if (painted) setSkeletonShown(true);
  }, [painted]);

  // Redirects don't wait for the hold: the data is in, and the skeleton
  // was never this destination's frame.
  if (recipeQuery.data !== undefined) {
    const landed = resolveHomeSurface(recipeQuery.data);
    if (landed === "group-cartera") return <Navigate to="/proyectos" replace />;
  }

  if (view.kind === "loading") {
    return (
      <div className="page">
        <OverviewHeader pending neutral skelVisible={view.visible} />
        <PageSkeleton label="Cargando el dashboard…" visible={view.visible}>
          <OverviewSkeleton labels={false} />
        </PageSkeleton>
      </div>
    );
  }

  if (view.kind === "error") {
    return (
      <div className="page">
        <OverviewHeader neutral />
        <div className="panel">
          <PageError
            title={view.offline ? "Sin conexión" : "No pudimos cargar el dashboard"}
            description={
              view.offline
                ? "Esta información aún no está guardada en este dispositivo. Conéctate para verla."
                : "Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
            }
            onRetry={view.retry}
          />
        </div>
      </div>
    );
  }

  // `empty` can't happen (no isEmpty) and neither can `disabled` (useRecipe
  // is always enabled); if either ever does, no recipe is on its way —
  // the «sin tablero» state, never a skeleton forever.
  if (view.kind !== "ready") return <NoDashboardSurface />;

  // Redirect surfaces were handled above, with the data in hand.
  const surface = resolveHomeSurface(view.data);
  if (surface === "facility-overview") return <Overview skeletonShown={skeletonShown} />;
  return <NoDashboardSurface />;
}
